import { useState } from "react";
import { EnvelopeSimple, Handshake, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitListingRequest } from "@/lib/api";

const initialForm = {
  contact_name: "",
  company_name: "",
  email: "",
  phone: "",
  facility_name: "",
  city: "",
  state: "",
  message: "",
};

const fieldConfig = [
  ["contact_name", "Contact name"],
  ["company_name", "Company name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["facility_name", "Facility name"],
  ["city", "City"],
  ["state", "State", 2],
];

const buildListingRequestPayload = (form) => ({
  ...form,
  state: form.state.toUpperCase(),
});

const ListingRequestCopy = () => (
  <div data-testid="listing-request-copy" className="flex flex-col justify-between border border-zinc-200 bg-white p-6">
    <div>
      <p data-testid="listing-request-eyebrow" className="text-xs font-bold uppercase tracking-[0.18em] text-[#0055FF]">Paid listing inquiries</p>
      <h2 data-testid="listing-request-title" className="mt-2 font-display text-3xl font-black tracking-normal text-[#09090B]">
        Contact us to add an approved location.
      </h2>
      <p data-testid="listing-request-description" className="mt-4 text-base leading-7 text-[#52525B]">
        Public location creation is locked. Submit your facility details and our team will follow up to review eligibility and next steps.
      </p>
    </div>
    <Handshake data-testid="listing-request-handshake-icon" className="mt-8 text-[#FF5500]" size={56} weight="fill" />
  </div>
);

const ListingRequestInput = ({ field, form, onChange }) => {
  const [key, label, maxLength] = field;
  return (
    <label data-testid={`listing-request-${key}-label`} className="block text-sm font-bold text-[#09090B]">
      {label}
      <input
        data-testid={`listing-request-${key}-input`}
        required={key !== "phone"}
        maxLength={maxLength || undefined}
        value={form[key]}
        onChange={(event) => onChange(key, event.target.value)}
        className="mt-2 h-11 w-full rounded-md border border-zinc-300 px-3 outline-none focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
      />
    </label>
  );
};

const ListingRequestMessage = ({ value, onChange }) => (
  <label data-testid="listing-request-message-label" className="block text-sm font-bold text-[#09090B] sm:col-span-2">
    Message
    <textarea
      data-testid="listing-request-message-textarea"
      value={value}
      onChange={(event) => onChange("message", event.target.value)}
      rows={4}
      className="mt-2 w-full resize-none rounded-md border border-zinc-300 px-3 py-3 outline-none focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF]"
    />
  </label>
);

const ListingRequestSubmitButton = ({ submitting }) => (
  <button
    data-testid="listing-request-submit-button"
    type="submit"
    disabled={submitting}
    className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#09090B] px-4 text-sm font-black text-white transition-colors duration-200 hover:bg-[#0055FF] disabled:cursor-not-allowed disabled:bg-zinc-400 sm:col-span-2"
  >
    <PaperPlaneTilt data-testid="listing-request-submit-icon" size={18} weight="bold" />
    {submitting ? "Sending..." : "Submit listing request"}
  </button>
);

export const AddFacilityForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const request = await submitListingRequest(buildListingRequestPayload(form));
      setForm(initialForm);
      toast.success("Listing request received", { description: `${request.facility_name} has been sent for follow-up.` });
    } catch (error) {
      toast.error("Request could not be submitted", { description: error.response?.data?.detail || "Please confirm required fields." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="listing-request-section" id="listing-request" className="border-t border-zinc-200 bg-[#F4F4F5] px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-[1600px] gap-6 lg:grid-cols-[0.8fr_1.2fr]">
        <ListingRequestCopy />

        <form data-testid="listing-request-form" onSubmit={handleSubmit} className="grid gap-3 border border-zinc-200 bg-white p-5 sm:grid-cols-2">
          <div data-testid="listing-request-form-heading" className="flex items-center gap-2 sm:col-span-2">
            <EnvelopeSimple data-testid="listing-request-envelope-icon" size={22} weight="bold" className="text-[#0055FF]" />
            <p data-testid="listing-request-form-title" className="font-display text-xl font-black tracking-normal">Request listing follow-up</p>
          </div>
          {fieldConfig.map((field) => <ListingRequestInput key={field[0]} field={field} form={form} onChange={updateForm} />)}
          <ListingRequestMessage value={form.message} onChange={updateForm} />
          <ListingRequestSubmitButton submitting={submitting} />
        </form>
      </div>
    </section>
  );
};